// Copyright (c) 2022-2023. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { AuthSessionData } from "../../../../../../../io/hyperify/frontend/hooks/useAuthSession";
import { MY_NO_CLIENT_FOUND_ARTICLE_CLASS_NAME } from "../../../../../core/constants/className";
import {
    T_MY_ORDER_VIEW_NO_CLIENT_FOUND_TEXT,
    T_MY_ORDER_VIEW_NO_CLIENT_FOUND_TITLE,
    T_MY_ORDER_VIEW_NO_CLIENT_SELECT_COMPANY,
    T_MY_ORDER_VIEW_NO_CLIENT_SELECT_OTHER,
    T_MY_ORDER_VIEW_NO_CLIENT_SELECT_PERSON
} from "../../../../../core/constants/translation";
import { Form } from "../../../../../../../io/hyperify/frontend/components/form/Form";
import { Button } from "../../../../../../../io/hyperify/frontend/components/button/Button";
import { ComponentUtils } from "../../../../../../../io/hyperify/frontend/components/utils/ComponentUtils";
import { ReactNode, useCallback, useState } from "react";
import { MyNewClientArticle } from "../newClient/MyNewClientArticle";
import { ClientDTO } from "../../../../../../../io/hyperify/core/store/types/client/ClientDTO";
import { LogService } from "../../../../../../../io/hyperify/core/LogService";
import { useLogoutCallback } from "../../../../../../../io/hyperify/frontend/hooks/useLogoutCallback";
import { ButtonStyle } from "../../../../../../../io/hyperify/core/frontend/button/ButtonStyle";
import "./MyNoClientFoundArticle.scss";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";

const LOG = LogService.createLogger('MyNoClientFoundArticle');

export interface MyNoClientFoundArticleProps {
    readonly className?: string;
    readonly children?: ReactNode;
    readonly t: TranslationFunction;
    readonly session: AuthSessionData;
    readonly foundClient : (client: ClientDTO) => void;
}

export function MyNoClientFoundArticle (props: MyNoClientFoundArticleProps) {
    const className = props?.className;
    const t = props?.t;
    const session = props?.session;
    const email = session?.email;
    const sms = session?.sms;
    const foundClientProp = props?.foundClient;
    const translationParams = {
        EMAIL: email,
        SMS: sms,
    };
    const [isCompany, setIsCompany] = useState<boolean | undefined>(undefined);
    const foundClient = useCallback(
        (client: ClientDTO) => {
            setIsCompany(undefined);
            if (foundClientProp) {
                LOG.debug(`Found client: `, client);
                foundClientProp(client);
            } else {
                LOG.debug(`No foundClientProp defined: `, client);
            }
        },
        [
            setIsCompany,
            foundClientProp
        ]
    );
    const createCompanyCallback = useCallback(() => setIsCompany(true), [setIsCompany]);
    const createPersonCallback = useCallback(() => setIsCompany(false), [setIsCompany]);
    const unSelectCallback = useCallback(() => { setIsCompany(undefined); }, [ setIsCompany ] );
    const logoutCallback = useLogoutCallback('MyNoClientFoundArticle');

    if (isCompany !== undefined) {
        return (
            <MyNewClientArticle
                className={className}
                t={t}
                session={session}
                isCompany={isCompany}
                backAction={unSelectCallback}
                submitAction={foundClient}
            />
        );
    }

    return (
        <article
            className={
                MY_NO_CLIENT_FOUND_ARTICLE_CLASS_NAME
                + (className ? ` ${ className }` : '')
            }
        >
            <Form className={ `${ MY_NO_CLIENT_FOUND_ARTICLE_CLASS_NAME }-form` }>
                <h3>{ t( T_MY_ORDER_VIEW_NO_CLIENT_FOUND_TITLE, translationParams ) }</h3>
                <p>{ ComponentUtils.splitWithLineBreaks( t( T_MY_ORDER_VIEW_NO_CLIENT_FOUND_TEXT, translationParams ) ) }</p>
                <div className={ `${ MY_NO_CLIENT_FOUND_ARTICLE_CLASS_NAME }-buttons` }>
                    <Button
                        className={ "button" }
                        click={ createCompanyCallback }
                    >{ t( T_MY_ORDER_VIEW_NO_CLIENT_SELECT_COMPANY, translationParams ) }</Button>
                    <Button
                        className={ "button" }
                        click={ createPersonCallback }
                    >{ t( T_MY_ORDER_VIEW_NO_CLIENT_SELECT_PERSON, translationParams ) }</Button>
                </div>
                <div className={ `${ MY_NO_CLIENT_FOUND_ARTICLE_CLASS_NAME }-buttons` }>
                    <Button
                        style={ ButtonStyle.SECONDARY }
                        className={ "button" }
                        click={ logoutCallback }
                    >{ t( T_MY_ORDER_VIEW_NO_CLIENT_SELECT_OTHER, translationParams ) }</Button>
                </div>
            </Form>
            <div className={ `${ MY_NO_CLIENT_FOUND_ARTICLE_CLASS_NAME }-buttons` }>
                { props?.children }
            </div>
        </article>
    );
}
